import { appIntl } from '@nxsys/helpers/Common';
import { showMessage } from '@nxsys/utils/message';
import { saveFile } from '@nxsys/utils/save-file';

// NX-5603: Download All on the Payment Summary / Draft Payroll reports builds
// its ZIP in a background job. The tab that asked shows how far the job has
// got and saves the file when it is ready; the notification bell keeps a link
// to it for later.

export const DOWNLOAD_ALL_PAYSLIPS = 'DOWNLOAD_ALL_PAYSLIPS';

// What the job sends on `download-all-payslips-<user id>`.
export type PayslipZipEvent = {
  jobId: number;
  status: 'PROGRESS' | 'READY' | 'FAILED';
  done: number;
  total: number;
  failed?: number;
  fileName?: string;
  urlFile?: string;
};

// The bell entry of a finished Download All carries its ZIP.
export const isPayslipZipNotification = (item?: {
  notificationType?: string;
  urlFile?: string;
}) => item?.notificationType === DOWNLOAD_ALL_PAYSLIPS && !!item.urlFile;

export const payslipZipChannelOf = (userId?: string) =>
  userId ? `download-all-payslips-${userId}` : undefined;

// No word from the job for this long (it is queued behind another export, or
// the socket dropped) and the user is told where the file will turn up.
export const QUIET_AFTER_MS = 2 * 60 * 1000;

// Job id -> the timer that fires when the job has gone quiet.
const pending = new Map<number, ReturnType<typeof setTimeout>>();

// A small job can finish before the request that queued it has answered, so
// the tab does not know its id yet. The latest word from a job this tab is not
// following is kept, and played back if the tab starts following it. No clock:
// the answer can come any time later. Only this many jobs are kept, far more
// than one user has in flight at once; the first one heard of goes first.
export const EARLY_JOBS_KEPT = 50;

// Job id -> that latest word.
const early = new Map<number, PayslipZipEvent>();

const keepEarly = (event: PayslipZipEvent) => {
  early.set(event.jobId, event);
  if (early.size > EARLY_JOBS_KEPT) {
    const [oldest] = early.keys();
    early.delete(oldest);
  }
};

const messageKey = (jobId: number) => `download-all-payslips-${jobId}`;

const t = (id: string, values?: Record<string, string | number>) =>
  appIntl().formatMessage({ id }, values);

const showProgress = (jobId: number, done: number, total: number) =>
  showMessage({
    type: 'loading',
    key: messageKey(jobId),
    duration: 0,
    content: t('downloadAll.preparing', { done, total }),
  });

const waitForNews = (jobId: number) => {
  clearTimeout(pending.get(jobId));
  pending.set(
    jobId,
    setTimeout(() => {
      showMessage({
        type: 'info',
        key: messageKey(jobId),
        duration: 10,
        content: t('downloadAll.stillPreparing'),
      });
    }, QUIET_AFTER_MS),
  );
};

export const trackPayslipZip = (jobId: number, total: number) => {
  showProgress(jobId, 0, total);
  waitForNews(jobId);
  const missed = early.get(jobId);
  early.delete(jobId);
  if (missed) onPayslipZipEvent(missed);
};

export const onPayslipZipEvent = (event: PayslipZipEvent) => {
  if (!event) return;
  // Another tab's download, one this tab already finished, or one whose
  // request has not answered yet.
  if (!pending.has(event.jobId)) {
    keepEarly(event);
    return;
  }
  const { jobId } = event;
  if (event.status === 'PROGRESS') {
    showProgress(jobId, event.done, event.total);
    waitForNews(jobId);
    return;
  }
  clearTimeout(pending.get(jobId));
  pending.delete(jobId);
  if (event.status === 'READY' && event.urlFile) {
    saveFile(event.urlFile);
    showMessage(
      event.failed
        ? {
            type: 'warning',
            key: messageKey(jobId),
            duration: 10,
            content: t('downloadAll.readyWithErrors', { failed: event.failed }),
          }
        : {
            type: 'success',
            key: messageKey(jobId),
            duration: 5,
            content: t('downloadAll.ready'),
          },
    );
    return;
  }
  showMessage({
    type: 'error',
    key: messageKey(jobId),
    duration: 10,
    content: t('downloadAll.failed'),
  });
};
